import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { useMemo, useRef, useState, type FormEvent } from "react";
import { useUser, useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/Button";
import { CategoryIcon } from "@/components/ui/CategoryIcon";
import { Field, FormError, Input, Select, Textarea } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Feedback";
import { Segmented } from "@/components/ui/Layout";
import { ApiError } from "@/lib/api";
import {
  useAccounts, useCategories, useCreateTransaction, useMerchants, useUpdateTransaction,
} from "@/lib/queries";
import type { Category, Transaction, TransactionType } from "@/lib/types";

export interface TransactionFormProps {
  /** Present when editing. */
  transaction?: Transaction;
  /** Defaults for a new transaction. */
  defaults?: {
    transaction_type?: TransactionType;
    account_id?: string;
    original?: Transaction;
  };
  onDone: () => void;
}

const AMOUNT = /^\d{1,15}(\.\d{1,4})?$/;
const MAIN_TYPES: { value: TransactionType; label: string }[] = [
  { value: "expense", label: "Expense" },
  { value: "income", label: "Income" },
  { value: "transfer", label: "Transfer" },
];

interface FormState {
  transaction_type: TransactionType;
  amount: string;
  account_id: string;
  counter_account_id: string;
  category_id: string;
  transaction_date: string;
  description: string;
  merchant_name: string;
  reference: string;
  notes: string;
  adjustment_direction: "increase" | "decrease";
  original_transaction_id: string;
}

export function TransactionForm({ transaction, defaults, onDone }: TransactionFormProps) {
  const user = useUser();
  const { today } = useAuth();
  const toast = useToast();
  const accounts = useAccounts();
  const categories = useCategories();
  const editing = Boolean(transaction);
  // One id per form: the transaction's id for good, so a retried or double submit can only ever
  // record one transaction (and the server recognises the id if a retry crosses the network).
  const entityId = useRef(crypto.randomUUID());
  const create = useCreateTransaction();
  const update = useUpdateTransaction();
  const saving = create.isPending || update.isPending;

  const activeAccounts = (accounts.data ?? []).filter(
    (a) => a.is_active || a.id === transaction?.account.id || a.id === transaction?.counter_account?.id,
  );
  const defaultAccount =
    defaults?.account_id ??
    activeAccounts.find((a) => a.id === user.default_account_id)?.id ??
    activeAccounts[0]?.id ??
    "";

  const original = defaults?.original;
  const [form, setForm] = useState<FormState>(() => ({
    transaction_type: transaction?.transaction_type ?? defaults?.transaction_type ?? "expense",
    amount: transaction ? Number(transaction.amount).toString() : original ? Number(original.amount).toString() : "",
    account_id: transaction?.account.id ?? original?.account.id ?? defaultAccount,
    counter_account_id: transaction?.counter_account?.id ?? "",
    category_id: transaction?.category?.id ?? original?.category?.id ?? "",
    transaction_date: transaction?.transaction_date ?? today,
    description: transaction?.description ?? (original ? `Refund: ${original.description}` : ""),
    merchant_name: transaction?.merchant?.name ?? original?.merchant?.name ?? "",
    reference: transaction?.reference ?? "",
    notes: transaction?.notes ?? "",
    adjustment_direction: transaction?.adjustment_direction ?? "decrease",
    original_transaction_id: transaction?.original_transaction_id ?? original?.id ?? "",
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(
    editing || form.transaction_type === "refund" || form.transaction_type === "adjustment",
  );

  // Account list can arrive after first render: fill the default once it does.
  if (!form.account_id && defaultAccount) setForm((f) => ({ ...f, account_id: defaultAccount }));

  const type = form.transaction_type;
  const needsCategory = type === "income" || type === "expense" || type === "refund";
  const categoryType = type === "income" ? "income" : "expense";
  const categoryOptions = useMemo(() => orderCategories(
    (categories.data ?? []).filter(
      (c) => c.category_type === categoryType && (c.is_active || c.id === form.category_id),
    ),
  ), [categories.data, categoryType, form.category_id]);
  const selectedAccount = activeAccounts.find((a) => a.id === form.account_id);
  const currency = selectedAccount?.currency ?? user.base_currency;

  const merchantQuery = useMerchants(form.merchant_name.length >= 2 ? form.merchant_name : "");

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => {
      const { [key]: _removed, ...rest } = e;
      return rest;
    });
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    const amount = form.amount.replace(/,/g, "").trim();
    if (!amount) e.amount = "Enter an amount";
    else if (!AMOUNT.test(amount) || Number(amount) <= 0) e.amount = "Enter a positive amount (up to 4 decimals)";
    if (!form.account_id) e.account_id = "Choose an account";
    if (needsCategory && !form.category_id && !(type === "refund" && form.original_transaction_id))
      e.category_id = "Choose a category";
    if (type === "transfer") {
      if (!form.counter_account_id) e.counter_account_id = "Choose where the money went";
      else if (form.counter_account_id === form.account_id) e.counter_account_id = "Pick a different account";
    }
    if (!form.transaction_date) e.transaction_date = "Choose a date";
    return e;
  }

  /** Saves to this device (and the sync queue) at once; the server confirms when it can. */
  function save() {
    const amount = form.amount.replace(/,/g, "").trim();
    const common = {
      transaction_date: form.transaction_date,
      amount,
      account_id: form.account_id,
      description: form.description.trim(),
      reference: form.reference.trim() || null,
      notes: form.notes.trim() || null,
    };
    const typed = {
      ...(needsCategory && form.category_id ? { category_id: form.category_id } : {}),
      ...(type === "transfer" ? { counter_account_id: form.counter_account_id } : {}),
      ...(type === "adjustment" ? { adjustment_direction: form.adjustment_direction } : {}),
      ...(type === "refund" && form.original_transaction_id
        ? { original_transaction_id: form.original_transaction_id } : {}),
      ...(type === "income" || type === "expense" || type === "refund"
        ? { merchant_name: form.merchant_name.trim() || null } : {}),
    };
    if (transaction) {
      return update.mutateAsync({
        id: transaction.id,
        changes: { ...common, ...typed },
        expectedVersion: transaction.version,
      });
    }
    return create.mutateAsync({ id: entityId.current, input: { transaction_type: type, ...common, ...typed } });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;
    try {
      const outcome = await save();
      toast(outcome.queued
        ? "Saved on this device. It will sync automatically."
        : editing ? "Transaction updated" : "Transaction saved");
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length ? null : err.message);
        if (err.code === "STALE_WRITE") setFormError("Someone changed this transaction. Close and reopen it to see the latest version.");
      } else {
        setFormError("Could not save. Try again.");
      }
    }
  }

  const otherAccounts = activeAccounts.filter((a) => a.id !== form.account_id);

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      {!editing && (
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            label="Transaction type"
            value={MAIN_TYPES.some((t) => t.value === type) ? type : ("" as TransactionType)}
            options={MAIN_TYPES}
            onChange={(v) => set("transaction_type", v)}
            className="flex-1"
          />
          <Select
            aria-label="Other types"
            value={type === "refund" || type === "adjustment" ? type : ""}
            onChange={(e) => {
              if (e.target.value) {
                set("transaction_type", e.target.value as TransactionType);
                setShowDetails(true);
              }
            }}
            className="!h-9 !w-auto text-sm"
          >
            <option value="">More…</option>
            <option value="refund">Refund</option>
            <option value="adjustment">Adjustment</option>
          </Select>
        </div>
      )}

      <Field label="Amount" error={errors.amount}>
        {({ id, describedBy, invalid }) => (
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-3">
              {currency}
            </span>
            <input
              id={id}
              data-autofocus
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={form.amount}
              onChange={(e) => set("amount", e.target.value.replace(/[^\d.,]/g, ""))}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              className="num h-14 w-full rounded-xl border border-border bg-surface pl-14 pr-3 text-2xl font-semibold text-ink focus:border-accent focus:outline-none aria-[invalid=true]:border-status-critical"
            />
          </div>
        )}
      </Field>

      <div className={clsx("grid gap-3", type === "transfer" && "sm:grid-cols-2")}>
        <Field label={type === "transfer" ? "From" : "Account"} error={errors.account_id}>
          {({ id, describedBy, invalid }) => (
            <Select id={id} value={form.account_id} aria-invalid={invalid} aria-describedby={describedBy}
                    onChange={(e) => set("account_id", e.target.value)}>
              {activeAccounts.length === 0 && <option value="">No accounts yet</option>}
              {activeAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>
              ))}
            </Select>
          )}
        </Field>
        {type === "transfer" && (
          <Field label="To" error={errors.counter_account_id}>
            {({ id, describedBy, invalid }) => (
              <Select id={id} value={form.counter_account_id} aria-invalid={invalid}
                      aria-describedby={describedBy}
                      onChange={(e) => set("counter_account_id", e.target.value)}>
                <option value="">Choose account</option>
                {otherAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </div>

      {needsCategory && (
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium text-ink-2">Category</legend>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Category">
            {categoryOptions.map((c) => (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={form.category_id === c.id}
                onClick={() => set("category_id", c.id)}
                className={clsx(
                  "inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm",
                  form.category_id === c.id
                    ? "border-accent bg-accent-wash font-medium text-ink"
                    : "border-border text-ink-2 hover:bg-surface-2",
                  c.parent_id && "pl-2.5",
                )}
              >
                <CategoryIcon slug={c.icon} className="size-3.5" />
                {c.name}
              </button>
            ))}
          </div>
          {errors.category_id && (
            <p role="alert" className="mt-1.5 text-xs font-medium text-bad">{errors.category_id}</p>
          )}
        </fieldset>
      )}

      {type === "adjustment" && (
        <Segmented
          label="Adjustment direction"
          value={form.adjustment_direction}
          options={[
            { value: "decrease", label: "Decrease balance" },
            { value: "increase", label: "Increase balance" },
          ]}
          onChange={(v) => set("adjustment_direction", v)}
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Date" error={errors.transaction_date}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} type="date" value={form.transaction_date} aria-invalid={invalid}
                   aria-describedby={describedBy}
                   onChange={(e) => set("transaction_date", e.target.value)} />
          )}
        </Field>
        {(type === "income" || type === "expense" || type === "refund") && (
          <Field label="Merchant / payee" error={errors.merchant_name}>
            {({ id, describedBy }) => (
              <>
                <Input id={id} list={`${id}-list`} value={form.merchant_name} aria-describedby={describedBy}
                       autoComplete="off" placeholder="e.g. Naivas"
                       onChange={(e) => set("merchant_name", e.target.value)} />
                <datalist id={`${id}-list`}>
                  {(merchantQuery.data ?? []).map((m) => <option key={m.id} value={m.name} />)}
                </datalist>
              </>
            )}
          </Field>
        )}
      </div>

      <button
        type="button"
        onClick={() => setShowDetails((s) => !s)}
        aria-expanded={showDetails}
        className="inline-flex items-center gap-1 self-start text-sm font-medium text-ink-2 hover:text-ink"
      >
        <ChevronDown className={clsx("size-4", showDetails && "rotate-180")} aria-hidden />
        {showDetails ? "Fewer details" : "Description, reference, notes"}
      </button>

      {showDetails && (
        <div className="grid gap-3">
          <Field label="Description" error={errors.description}>
            {({ id, describedBy }) => (
              <Input id={id} value={form.description} maxLength={500} aria-describedby={describedBy}
                     onChange={(e) => set("description", e.target.value)} />
            )}
          </Field>
          <Field label="Reference" hint="Receipt or confirmation code (e.g. M-Pesa code)" error={errors.reference}>
            {({ id, describedBy }) => (
              <Input id={id} value={form.reference} maxLength={100} aria-describedby={describedBy}
                     onChange={(e) => set("reference", e.target.value)} />
            )}
          </Field>
          <Field label="Notes" error={errors.notes}>
            {({ id, describedBy }) => (
              <Textarea id={id} value={form.notes} maxLength={2000} aria-describedby={describedBy}
                        onChange={(e) => set("notes", e.target.value)} />
            )}
          </Field>
          {type === "refund" && original && (
            <p className="text-sm text-ink-3">
              Refund of “{original.description || original.category?.name}” on {original.transaction_date}.
            </p>
          )}
        </div>
      )}

      <FormError message={formError} />

      <div className="sticky bottom-0 -mx-5 flex gap-2 border-t border-border bg-surface px-5 pt-3 sm:static sm:mx-0 sm:border-0 sm:px-0 sm:pt-0">
        <Button variant="ghost" onClick={onDone} className="sm:ml-auto">Cancel</Button>
        <Button type="submit" variant="primary" size="lg" loading={saving} className="flex-1 sm:flex-none">
          {editing ? "Save changes" : "Save"}
        </Button>
      </div>
    </form>
  );
}

/** Parents first, each followed by its children. */
function orderCategories(list: Category[]): Category[] {
  const roots = list.filter((c) => !c.parent_id || !list.some((p) => p.id === c.parent_id));
  const out: Category[] = [];
  for (const root of roots) {
    out.push(root);
    out.push(...list.filter((c) => c.parent_id === root.id));
  }
  return out;
}
